import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Plus, X, Lock, Info } from "lucide-react";
import { PageShell } from "@/components/SidebarLayout";
import { api, formatApiError } from "@/lib/api";
import { monthIsoToInput, monthInputToIso, formatPct } from "@/lib/format";
import { TEST_IDS } from "@/testIds";

export default function Settings() {
  const [bookingPct, setBookingPct] = useState(0.10);
  const [nextPct, setNextPct] = useState(0.20);
  const [nextLag, setNextLag] = useState(1);
  const [asOf, setAsOf] = useState("2026-07-01");
  const [openingBalance, setOpeningBalance] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      setBookingPct(data.payment_plan.booking_pct);
      setNextPct(data.payment_plan.next_pct);
      setNextLag(data.payment_plan.next_lag_months);
      setAsOf(data.as_of);
    });
    api.get("/treasury/settings").then(({ data }) => {
      setOpeningBalance(data.opening_balance ?? 0);
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await Promise.all([
        api.put("/settings/payment-plan", {
          booking_pct: parseFloat(bookingPct) || 0,
          next_pct: parseFloat(nextPct) || 0,
          next_lag_months: parseInt(nextLag) || 1,
        }),
        api.put("/settings/as-of", { as_of: asOf }),
        api.put("/treasury/settings", { opening_balance: parseFloat(openingBalance) || 0 }),
      ]);
      toast.success("Settings saved", { description: "Projections & flags refreshed." });
    } catch (e) {
      toast.error("Save failed", { description: e.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  const total = ((parseFloat(bookingPct) || 0) + (parseFloat(nextPct) || 0)) * 100;

  return (
    <PageShell testId="settings-page" kicker="Configuration" title="Settings">
      <p className="-mt-4 text-muted-dark text-sm max-w-2xl">
        Global rules used by the projection engine. Changes here re-run
        projections and flags across every project.
      </p>

      <div className="cf-card cf-fade-up p-6 md:p-8 max-w-3xl space-y-8">
        <section>
          <h2 className="font-display text-xl text-ink mb-1">Payment Plan</h2>
          <p className="text-xs text-muted-dark mb-5">
            Percentage of sales value that lands as a collection at booking
            month and next month. Milestones fill the remaining ~60%.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Booking month %" hint="Fraction: 0.10 = 10%">
              <input
                data-testid={TEST_IDS.settingsBookingPct}
                type="number"
                step="0.001"
                value={bookingPct}
                onChange={(e) => setBookingPct(e.target.value)}
                className="w-full px-3 py-2 border border-hairline rounded-md bg-paper text-ink num focus:outline-none focus:border-ink"
              />
            </Field>
            <Field label="Next month %" hint="Fraction: 0.20 = 20%">
              <input
                data-testid={TEST_IDS.settingsNextPct}
                type="number"
                step="0.001"
                value={nextPct}
                onChange={(e) => setNextPct(e.target.value)}
                className="w-full px-3 py-2 border border-hairline rounded-md bg-paper text-ink num focus:outline-none focus:border-ink"
              />
            </Field>
            <Field label="Next-month lag" hint="Months after booking">
              <input
                type="number"
                min="0"
                step="1"
                value={nextLag}
                onChange={(e) => setNextLag(e.target.value)}
                className="w-full px-3 py-2 border border-hairline rounded-md bg-paper text-ink num focus:outline-none focus:border-ink"
              />
            </Field>
          </div>

          <div className="mt-4 text-xs text-muted-dark flex items-center gap-4">
            <span>
              Sales-linked total:{" "}
              <span className="num text-ink">{total.toFixed(1)}%</span>{" "}
              <span className="text-muted-light">
                (milestones should sum to {(100 - total).toFixed(1)}% for new projects)
              </span>
            </span>
          </div>
        </section>

        <section className="pt-6 border-t border-hairline">
          <h2 className="font-display text-xl text-ink mb-1">Projection Anchor</h2>
          <p className="text-xs text-muted-dark mb-5">
            The month the horizon selector counts forward from. Change this to
            simulate cashflow from a different &quot;today&quot;.
          </p>
          <Field label="As-of month" hint="Applied to every dashboard load">
            <input
              data-testid={TEST_IDS.settingsAsOf}
              type="month"
              value={monthIsoToInput(asOf)}
              onChange={(e) => setAsOf(monthInputToIso(e.target.value))}
              className="w-full max-w-[220px] px-3 py-2 border border-hairline rounded-md bg-paper text-ink num focus:outline-none focus:border-ink"
            />
          </Field>
        </section>

        <section className="pt-6 border-t border-hairline">
          <h2 className="font-display text-xl text-ink mb-1">Company Treasury</h2>
          <p className="text-xs text-muted-dark mb-5">
            First-month opening balance (in ₹ Lakhs). Every subsequent month&apos;s
            opening auto-rolls from the previous month&apos;s closing on the
            Cashflow Overview — no per-month editing needed.
          </p>
          <Field label="Opening balance — first horizon month (₹L)" hint={`e.g. ₹13,904 L = ₹139.04 Cr`}>
            <input
              data-testid="settings-opening-balance"
              type="number"
              step="0.01"
              value={openingBalance}
              onChange={(e) => setOpeningBalance(e.target.value)}
              className="w-full max-w-[260px] px-3 py-2 border border-hairline rounded-md bg-paper text-ink num focus:outline-none focus:border-ink"
            />
          </Field>
        </section>

        <div className="pt-6 border-t border-hairline">
          <button
            data-testid={TEST_IDS.settingsSave}
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-ink text-paper text-sm hover:bg-ink/90 disabled:opacity-50 transition-colors"
          >
            <Save size={14} /> {saving ? "Saving…" : "Save & re-run engine"}
          </button>
        </div>
      </div>

      <BulkPaymentPlanSection />

      <ProjectsMasterSection />

      <OpeningPositionSection />

      <MilestoneMasterSection />
    </PageShell>
  );
}

// ---------------- Projects Master (Add / edit / list) ---------------- //
function ProjectsMasterSection() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ name: "", type: "new", status: "active" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
    } catch (e) {
      toast.error("Failed to load projects", { description: formatApiError(e.response?.data?.detail) });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setDraft({ name: "", type: "new", status: "active" }); setCreating(true); };
  const cancel = () => setCreating(false);

  const save = async () => {
    const name = (draft.name || "").trim();
    if (!name) return toast.error("Name is required");
    setBusy(true);
    try {
      await api.post("/projects", { name, type: draft.type, status: draft.status });
      toast.success(`${name} added — visible across Sales, Milestones, Loan Facilities, Cashflow`);
      setCreating(false);
      await load();
    } catch (e) {
      toast.error("Save failed", { description: formatApiError(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  return (
    <div className="cf-card cf-fade-up p-6 md:p-8 max-w-5xl space-y-4" data-testid="projects-master-section">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-xl text-ink mb-1">Projects</h2>
          <p className="text-xs text-muted-dark max-w-xl">
            The master list of projects. Adding one here surfaces it in Sales Bookings,
            Collections Model, Milestones, Loan Facility pickers, and the Cashflow ledger.
          </p>
        </div>
        <button
          data-testid="project-add-btn"
          onClick={startCreate}
          disabled={creating}
          className="cf-touch inline-flex items-center gap-1.5 px-3.5 py-2 text-sm rounded-full bg-ink text-paper disabled:opacity-50"
          style={{ boxShadow: "0 4px 12px rgba(18,49,89,0.15)" }}
        >
          <Plus size={13} /> Add Project
        </button>
      </div>

      <div className="rounded-lg border border-hairline/60 overflow-x-auto">
        <table className="w-full min-w-[560px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-muted-light border-b border-hairline/60" style={{ background: "var(--cf-surface-2)" }}>
              <th className="text-left font-semibold px-4 py-2">Name</th>
              <th className="text-center font-semibold px-3 py-2">Type</th>
              <th className="text-center font-semibold px-3 py-2">Status</th>
              <th className="text-right font-semibold px-3 py-2 num">Baseline (₹L)</th>
            </tr>
          </thead>
          <tbody>
            {creating && (
              <tr className="bg-gold-500/10 border-b border-hairline/40" data-testid="project-new-row">
                <td className="px-3 py-1.5 align-middle">
                  <input
                    data-testid="project-input-name"
                    autoFocus
                    value={draft.name}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    placeholder="e.g. Willow Grove"
                    className="cf-touch w-full px-2 py-1.5 bg-surface rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ink/15"
                  />
                </td>
                <td className="px-3 py-1.5 align-middle text-center">
                  <select
                    data-testid="project-input-type"
                    value={draft.type}
                    onChange={(e) => setDraft({ ...draft, type: e.target.value })}
                    className="cf-touch px-2 py-1.5 bg-surface rounded-lg text-xs"
                  >
                    <option value="new">new</option>
                    <option value="ongoing">ongoing</option>
                    <option value="land">land</option>
                  </select>
                </td>
                <td className="px-3 py-1.5 align-middle text-center">
                  <select
                    data-testid="project-input-status"
                    value={draft.status}
                    onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                    className="cf-touch px-2 py-1.5 bg-surface rounded-lg text-xs"
                  >
                    <option value="active">active</option>
                    <option value="hold">hold</option>
                    <option value="closed">closed</option>
                  </select>
                </td>
                <td className="px-3 py-1.5 text-right whitespace-nowrap space-x-1 align-middle">
                  <button data-testid="project-save-btn" onClick={save} disabled={busy} className="px-2.5 py-1.5 text-xs rounded-md bg-ink text-paper disabled:opacity-50">
                    {busy ? "Saving…" : "Save"}
                  </button>
                  <button data-testid="project-cancel-btn" onClick={cancel} className="px-2.5 py-1.5 text-xs rounded-md text-muted-dark">Cancel</button>
                </td>
              </tr>
            )}
            {loading && !creating && (
              <tr><td colSpan={4} className="px-4 py-6 text-center text-muted-light text-sm">Loading…</td></tr>
            )}
            {!loading && projects.map((p, i) => (
              <tr key={p.id} data-testid={`project-row-${p.name}`} className="border-b border-hairline/40 last:border-0" style={{ background: i % 2 === 0 ? "var(--cf-surface)" : "var(--cf-surface-2)" }}>
                <td className="px-4 py-1.5 text-sm text-ink font-medium">{p.name}</td>
                <td className="px-3 py-1.5 text-center"><span className="cf-pill cf-pill-ink">{p.type}</span></td>
                <td className="px-3 py-1.5 text-center text-xs text-muted-dark">{p.status || "active"}</td>
                <td className="px-3 py-1.5 text-right num text-xs text-muted-dark">{(p.baseline_sales_booked ?? 0).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-xs text-muted-dark font-medium mb-1.5">{label}</label>
      {children}
      {hint && <div className="mt-1 text-[11px] text-muted-light">{hint}</div>}
    </div>
  );
}

// ---------------- Bulk Payment-Plan Editor ---------------- //
function BulkPaymentPlanSection() {
  const [projects, setProjects] = useState([]);
  const [globalDefault, setGlobalDefault] = useState({ booking_pct: 0.10, next_pct: 0.20, next_lag_months: 1 });
  const [drafts, setDrafts] = useState({});           // { [projectId]: {booking_pct, next_pct, next_lag_months} }
  const [saving, setSaving] = useState(null);         // projectId currently saving
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: projs }, { data: settings }] = await Promise.all([
        api.get("/projects"),
        api.get("/settings"),
      ]);
      setGlobalDefault(settings.payment_plan);
      const nonLand = projs.filter((p) => p.type !== "land");
      setProjects(nonLand);
      const nextDrafts = {};
      for (const p of nonLand) {
        const ov = p.payment_plan_override || {};
        nextDrafts[p.id] = {
          booking_pct: ov.booking_pct ?? settings.payment_plan.booking_pct,
          next_pct: ov.next_pct ?? settings.payment_plan.next_pct,
          next_lag_months: ov.next_lag_months ?? settings.payment_plan.next_lag_months,
          overridden: !!p.payment_plan_override,
        };
      }
      setDrafts(nextDrafts);
    } catch (e) {
      toast.error("Failed to load projects", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const patch = (id, field, value) => {
    setDrafts((d) => ({ ...d, [id]: { ...d[id], [field]: value, overridden: true } }));
  };

  const savRow = async (p) => {
    const d = drafts[p.id];
    setSaving(p.id);
    try {
      await api.put(`/projects/${p.id}/payment-plan`, {
        booking_pct: parseFloat(d.booking_pct),
        next_pct: parseFloat(d.next_pct),
        next_lag_months: parseInt(d.next_lag_months) || 1,
      });
      toast.success(`${p.name}: payment plan saved`);
      await load();
    } catch (e) {
      toast.error("Save failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setSaving(null);
    }
  };

  const clearRow = async (p) => {
    if (!window.confirm(`Reset ${p.name} to the global default (${(globalDefault.next_pct * 100).toFixed(1)}%)?`)) return;
    setSaving(p.id);
    try {
      await api.put(`/projects/${p.id}/payment-plan`, {
        booking_pct: null, next_pct: null, next_lag_months: null,
      });
      toast.success(`${p.name}: reset to global default`);
      await load();
    } catch (e) {
      toast.error("Reset failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="cf-card cf-fade-up p-6 md:p-8 max-w-5xl" data-testid="bulk-payment-plan-section">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <h2 className="font-display text-xl text-ink mb-1">Per-Project Payment Plans</h2>
          <p className="text-xs text-muted-dark max-w-xl">
            Every non-land project uses 10% Booking + a project-specific
            Next-Month rate. Leave a row on the global default
            (<span className="num">{(globalDefault.next_pct * 100).toFixed(1)}%</span>)
            or override it here — changes re-run the engine on save.
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-hairline/60 overflow-x-auto">
        <table className="w-full min-w-[720px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-muted-light border-b border-hairline/60" style={{ background: "var(--cf-surface-2)" }}>
              <th className="text-left font-semibold px-4 py-2">Project</th>
              <th className="text-center font-semibold px-3 py-2">Type</th>
              <th className="text-right font-semibold px-3 py-2">Booking %</th>
              <th className="text-right font-semibold px-3 py-2">Next-Month %</th>
              <th className="text-right font-semibold px-3 py-2">Lag (m)</th>
              <th className="text-right font-semibold px-3 py-2">Source</th>
              <th className="text-right font-semibold px-3 py-2" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-muted-light text-sm">Loading…</td></tr>
            )}
            {!loading && projects.map((p, i) => {
              const d = drafts[p.id] || {};
              const isOverride = !!p.payment_plan_override;
              return (
                <tr key={p.id} className="border-b border-hairline/40 last:border-0" style={{ background: i % 2 === 0 ? "var(--cf-surface)" : "var(--cf-surface-2)" }}>
                  <td className="px-4 py-1.5 text-sm text-ink font-medium">{p.name}</td>
                  <td className="px-3 py-1.5 text-center">
                    <span className="cf-pill cf-pill-ink">{p.type}</span>
                  </td>
                  <td className="px-3 py-1.5">
                    <input
                      type="number" step="0.001" min="0" max="1"
                      value={d.booking_pct ?? 0.10}
                      onChange={(e) => patch(p.id, "booking_pct", e.target.value)}
                      className="cf-touch w-24 ml-auto block px-2 py-1.5 bg-surface rounded-lg text-sm num text-right focus:outline-none focus:ring-2 focus:ring-ink/15"
                    />
                  </td>
                  <td className="px-3 py-1.5">
                    <input
                      data-testid={`bulk-next-pct-${p.name}`}
                      type="number" step="0.001" min="0" max="1"
                      value={d.next_pct ?? 0.20}
                      onChange={(e) => patch(p.id, "next_pct", e.target.value)}
                      className="cf-touch w-24 ml-auto block px-2 py-1.5 bg-surface rounded-lg text-sm num text-right focus:outline-none focus:ring-2 focus:ring-ink/15"
                    />
                  </td>
                  <td className="px-3 py-1.5">
                    <input
                      type="number" min="0" step="1"
                      value={d.next_lag_months ?? 1}
                      onChange={(e) => patch(p.id, "next_lag_months", e.target.value)}
                      className="cf-touch w-16 ml-auto block px-2 py-1.5 bg-surface rounded-lg text-sm num text-right focus:outline-none focus:ring-2 focus:ring-ink/15"
                    />
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    {isOverride
                      ? <span className="cf-pill cf-pill-accent">Overridden</span>
                      : <span className="cf-pill cf-pill-ok">Global</span>}
                  </td>
                  <td className="px-3 py-1.5 text-right whitespace-nowrap space-x-1">
                    <button
                      data-testid={`bulk-save-${p.name}`}
                      onClick={() => savRow(p)}
                      disabled={saving === p.id}
                      className="px-2.5 py-1 text-xs rounded-md bg-ink text-paper disabled:opacity-50"
                    >
                      {saving === p.id ? "…" : "Save"}
                    </button>
                    {isOverride && (
                      <button
                        onClick={() => clearRow(p)}
                        disabled={saving === p.id}
                        className="px-2.5 py-1 text-xs rounded-md text-muted-dark hover:text-red-600"
                        title="Reset to global default"
                      >
                        Reset
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------- Milestone Master ---------------- //
function MilestoneMasterSection() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ name: "", default_pct: "", default_lag_months: 1 });
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/milestone-master");
      setRows(data);
    } catch (e) {
      toast.error("Failed to load milestone master", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setDraft({ name: "", default_pct: "", default_lag_months: 1 });
    setCreating(true);
  };
  const cancel = () => { setCreating(false); setDraft({ name: "", default_pct: "", default_lag_months: 1 }); };

  const save = async () => {
    const name = (draft.name || "").trim();
    if (!name) return toast.error("Name is required");
    setBusy(true);
    try {
      const body = {
        name,
        default_pct: draft.default_pct === "" ? null : parseFloat(draft.default_pct),
        default_lag_months: parseInt(draft.default_lag_months || 1),
      };
      const { data } = await api.post("/milestone-master", body);
      if (data.already_exists) {
        toast.info("A milestone with that name already exists in the master.");
      } else {
        toast.success("Custom milestone added to master");
      }
      cancel();
      await load();
    } catch (e) {
      toast.error("Save failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    if (!row.is_user_added) return;
    if (!window.confirm(`Remove "${row.name}" from the master list?`)) return;
    try {
      await api.delete(`/milestone-master/${row.id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error("Delete failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  const filtered = search.trim()
    ? rows.filter((r) => r.name.toLowerCase().includes(search.toLowerCase()))
    : rows;

  const stdCount = rows.filter((r) => r.is_standard).length;
  const userCount = rows.filter((r) => r.is_user_added).length;
  const usedCount = rows.filter((r) => !r.is_standard && !r.is_user_added).length;

  return (
    <div className="cf-card cf-fade-up p-6 md:p-8 max-w-5xl space-y-4" data-testid="milestone-master-section">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-xl text-ink mb-1">Milestone Master</h2>
          <p className="text-xs text-muted-dark max-w-xl">
            Catalog of milestone names known to the system. Standard rules are
            locked; user-added templates persist here and can be reused when
            creating project milestones.
          </p>
          <div className="mt-2 flex items-center gap-3 text-[11px] text-muted-light">
            <span>{stdCount} standard</span>
            <span className="w-1 h-1 rounded-full bg-muted-light" />
            <span>{userCount} user-added</span>
            <span className="w-1 h-1 rounded-full bg-muted-light" />
            <span>{usedCount} in use</span>
          </div>
        </div>
        <button
          data-testid="milestone-master-add-btn"
          onClick={startCreate}
          disabled={creating}
          className="cf-touch inline-flex items-center gap-1.5 px-3.5 py-2 text-sm rounded-full bg-ink text-paper disabled:opacity-50"
          style={{ boxShadow: "0 4px 12px rgba(18,49,89,0.15)" }}
        >
          <Plus size={13} /> Add Custom Milestone
        </button>
      </div>

      <div className="flex items-center gap-2">
        <input
          data-testid="milestone-master-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search milestone name…"
          className="cf-touch flex-1 max-w-sm px-3 py-2 text-sm bg-paper rounded-lg focus:outline-none focus:ring-2 focus:ring-ink/15"
        />
        <span className="text-[11px] text-muted-light num">{filtered.length} rows</span>
      </div>

      <div className="rounded-lg border border-hairline/60 overflow-x-auto">
        <table className="w-full min-w-[720px] table-fixed">
          <colgroup>
            <col className="w-auto" />
            <col style={{ width: 120 }} />
            <col style={{ width: 96 }} />
            <col style={{ width: 64 }} />
            <col style={{ width: 76 }} />
            <col style={{ width: 108 }} />
          </colgroup>
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-muted-light border-b border-hairline/60 bg-paper/40">
              <th className="sticky left-0 z-10 bg-paper/40 text-left font-semibold px-4 py-2 whitespace-nowrap">Milestone Name</th>
              <th className="text-center font-semibold px-3 py-2 whitespace-nowrap">Type</th>
              <th className="text-right font-semibold px-3 py-2 whitespace-nowrap">Default %</th>
              <th className="text-right font-semibold px-3 py-2 whitespace-nowrap">Lag</th>
              <th className="text-right font-semibold px-3 py-2 whitespace-nowrap">In Use</th>
              <th className="text-right font-semibold px-3 py-2 whitespace-nowrap" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {creating && (
              <tr className="bg-gold-500/10 border-b border-hairline/40" data-testid="milestone-master-new-row">
                <td className="px-3 py-1.5 align-middle">
                  <input
                    data-testid="milestone-master-input-name"
                    autoFocus
                    value={draft.name}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    placeholder="e.g. On Completion of RCC Roofing"
                    className="cf-touch w-full px-2 py-1.5 bg-surface rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ink/15"
                  />
                </td>
                <td className="px-3 py-1.5 text-center text-xs text-muted-dark align-middle">Custom</td>
                <td className="px-3 py-1.5 align-middle">
                  <input
                    data-testid="milestone-master-input-pct"
                    type="number"
                    step="0.001"
                    value={draft.default_pct}
                    onChange={(e) => setDraft({ ...draft, default_pct: e.target.value })}
                    placeholder="0.075"
                    className="cf-touch w-full px-2 py-1.5 bg-surface rounded-lg text-sm num text-right focus:outline-none focus:ring-2 focus:ring-ink/15"
                  />
                </td>
                <td className="px-3 py-1.5 align-middle">
                  <input
                    data-testid="milestone-master-input-lag"
                    type="number"
                    min="0"
                    value={draft.default_lag_months}
                    onChange={(e) => setDraft({ ...draft, default_lag_months: e.target.value })}
                    className="cf-touch w-full px-2 py-1.5 bg-surface rounded-lg text-sm num text-right focus:outline-none focus:ring-2 focus:ring-ink/15"
                  />
                </td>
                <td className="px-3 py-1.5 text-right text-xs text-muted-light align-middle">—</td>
                <td className="px-3 py-1.5 text-right whitespace-nowrap space-x-1 align-middle">
                  <button
                    data-testid="milestone-master-save-btn"
                    onClick={save}
                    disabled={busy}
                    className="px-2.5 py-1.5 text-xs rounded-md bg-ink text-paper disabled:opacity-50"
                  >
                    {busy ? "Saving…" : "Save"}
                  </button>
                  <button
                    data-testid="milestone-master-cancel-btn"
                    onClick={cancel}
                    className="px-2.5 py-1.5 text-xs rounded-md text-muted-dark"
                  >
                    Cancel
                  </button>
                </td>
              </tr>
            )}
            {loading && !creating && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-light text-sm">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && !creating && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-light text-sm">
                No milestones match &ldquo;{search}&rdquo;.
              </td></tr>
            )}
            {filtered.map((r, i) => (
              <tr
                key={r.id}
                data-testid={`milestone-master-row-${r.name}`}
                className={`border-b border-hairline/40 last:border-0 hover:bg-surface-2/60 ${r.is_standard ? "bg-paper/40" : (i % 2 === 0 ? "bg-surface" : "bg-paper/20")}`}
              >
                <td className="sticky left-0 z-10 px-4 py-1.5 text-sm text-ink font-medium align-middle" style={{ background: r.is_standard ? "var(--cf-accent-soft)" : (i % 2 === 0 ? "var(--cf-surface)" : "var(--cf-surface-2)") }}>
                  <span className="inline-flex items-center gap-1.5 max-w-full">
                    {r.is_standard && <Lock size={11} className="text-muted-light shrink-0" />}
                    <span className="truncate">{r.name}</span>
                  </span>
                </td>
                <td className="px-3 py-1.5 text-center align-middle">
                  {r.is_standard ? (
                    <span className="cf-pill cf-pill-ink">Standard</span>
                  ) : r.is_user_added ? (
                    <span className="cf-pill" style={{ background: "rgba(251,192,45,0.18)", color: "#8A6800" }}>User-added</span>
                  ) : (
                    <span className="cf-pill cf-pill-warn">Custom</span>
                  )}
                </td>
                <td className="px-3 py-1.5 text-right num text-sm text-ink align-middle">
                  {r.default_pct != null ? formatPct(r.default_pct) : <span className="text-muted-light">—</span>}
                </td>
                <td className="px-3 py-1.5 text-right num text-xs text-muted-dark align-middle">
                  {r.default_lag_months ?? "—"}
                </td>
                <td className="px-3 py-1.5 text-right num text-xs text-muted-dark align-middle">
                  {r.usage_count}
                </td>
                <td className="px-3 py-1.5 text-right whitespace-nowrap align-middle">
                  {r.is_user_added ? (
                    <button
                      data-testid={`milestone-master-delete-${r.name}`}
                      onClick={() => remove(r)}
                      className="cf-touch inline-flex items-center gap-1 px-2 py-1 text-xs text-muted-dark hover:text-red-600"
                      title="Remove from master"
                    >
                      <X size={11} /> Remove
                    </button>
                  ) : (
                    <span className="text-[11px] text-muted-light inline-flex items-center gap-1">
                      <Info size={10} /> {r.is_standard ? "Locked" : "In use"}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------- Project Opening Position (Outstanding anchor) ---------------- //
function OpeningPositionSection() {
  const [asOn, setAsOn] = useState("2026-08");
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [addSel, setAddSel] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: op }, { data: projs }] = await Promise.all([
        api.get("/settings/opening-position"),
        api.get("/projects"),
      ]);
      if (op.as_on_month) setAsOn(op.as_on_month.slice(0, 7));
      setRows(op.rows || []);
      setProjects(projs || []);
    } catch (e) {
      toast.error("Failed to load opening position", { description: formatApiError(e.response?.data?.detail) });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  let asOnLabel = asOn;
  try {
    asOnLabel = new Date(asOn + "-01").toLocaleDateString("en-IN", { month: "long", year: "numeric" });
  } catch { /* keep raw */ }

  const setCell = (project, field, val) =>
    setRows((prev) => prev.map((r) => (r.project === project ? { ...r, [field]: val } : r)));
  const removeRow = (project) => setRows((prev) => prev.filter((r) => r.project !== project));
  const addable = projects.filter((p) => !rows.some((r) => r.project === p.name));
  const addRow = () => {
    if (!addSel) return;
    setRows((prev) => [...prev, { project: addSel, sales_booked_till: "", demand_raised_till: "", outstanding: "", completion_pct: "" }]
      .sort((a, b) => a.project.localeCompare(b.project)));
    setAddSel("");
  };

  const num = (v) => parseFloat(v) || 0;
  const totals = rows.reduce((acc, r) => ({
    sales_booked_till: acc.sales_booked_till + num(r.sales_booked_till),
    demand_raised_till: acc.demand_raised_till + num(r.demand_raised_till),
    outstanding: acc.outstanding + num(r.outstanding),
  }), { sales_booked_till: 0, demand_raised_till: 0, outstanding: 0 });

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings/opening-position", {
        as_on_month: monthInputToIso(asOn),
        rows: rows.map((r) => ({
          project: r.project,
          sales_booked_till: num(r.sales_booked_till),
          demand_raised_till: num(r.demand_raised_till),
          outstanding: num(r.outstanding),
          completion_pct: r.completion_pct === "" || r.completion_pct == null ? null : num(r.completion_pct),
        })),
      });
      toast.success("Opening position saved", {
        description: `Collections Model re-anchored as on ${asOnLabel} — totals recomputed everywhere.`,
      });
      await load();
    } catch (e) {
      toast.error("Save failed", { description: formatApiError(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  return (
    <div className="cf-card cf-fade-up p-6 md:p-8 max-w-5xl space-y-4" data-testid="opening-position-section">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-xl text-ink mb-1">Project Opening Position <span className="text-sm text-muted-dark font-normal">(As per ERP)</span></h2>
          <p className="text-xs text-muted-dark max-w-xl">
            The anchor table for the Collections Model. The first month after the
            as-on month collects <b>10% booking + the full Outstanding</b>; every
            month after chains forward automatically (10% + next-month tranche).
            Update the month and values any time to re-anchor on fresh actuals.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-muted-light font-semibold mb-1">Values as on</label>
            <input
              data-testid="op-as-on-input"
              type="month"
              value={asOn}
              onChange={(e) => setAsOn(e.target.value)}
              className="cf-touch px-3 py-2 border border-hairline rounded-md bg-paper text-ink num text-sm focus:outline-none focus:border-ink"
            />
          </div>
          <button
            data-testid="op-save-btn"
            onClick={save}
            disabled={busy || loading}
            className="cf-touch inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-full bg-ink text-paper disabled:opacity-50"
            style={{ boxShadow: "0 4px 12px rgba(18,49,89,0.15)" }}
          >
            <Save size={13} /> {busy ? "Saving…" : "Save & re-anchor"}
          </button>
        </div>
      </div>

      <div className="rounded-lg border border-hairline/60 overflow-x-auto">
        <table className="w-full min-w-[720px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-muted-light border-b border-hairline/60" style={{ background: "var(--cf-surface-2)" }}>
              <th className="text-center font-semibold px-2 py-2 w-10">S.No</th>
              <th className="text-left font-semibold px-4 py-2">Project</th>
              <th className="text-right font-semibold px-3 py-2">Total Sales Booked till {asOnLabel} (₹L)</th>
              <th className="text-right font-semibold px-3 py-2">Cumulative Demand Raised till {asOnLabel} (₹L)</th>
              <th className="text-right font-semibold px-3 py-2">Outstanding as on {asOnLabel} (₹L)</th>
              <th className="text-right font-semibold px-3 py-2" title="Activates the completed-stage catch-up: new bookings pay (completed% − 10%) the following month, auto-rolling as milestones trigger">Completed Milestone % (as on {asOnLabel})</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-muted-light text-sm">Loading…</td></tr>
            )}
            {!loading && rows.map((r, i) => (
              <tr key={r.project} data-testid={`op-row-${r.project}`} className="border-b border-hairline/40" style={{ background: i % 2 === 0 ? "var(--cf-surface)" : "var(--cf-surface-2)" }}>
                <td className="px-2 py-1.5 text-center text-xs text-muted-light num">{i + 1}</td>
                <td className="px-4 py-1.5 text-sm text-ink font-medium">{r.project}</td>
                {["sales_booked_till", "demand_raised_till", "outstanding"].map((f) => (
                  <td key={f} className="px-3 py-1.5 text-right align-middle">
                    <input
                      data-testid={`op-${f.replace(/_till$/, "").replace(/_/g, "-")}-${r.project}`}
                      type="text"
                      inputMode="decimal"
                      value={r[f]}
                      onChange={(e) => setCell(r.project, f, e.target.value)}
                      placeholder="0"
                      className="cf-touch w-28 px-2 py-1.5 bg-surface rounded-lg text-sm text-right num focus:outline-none focus:ring-2 focus:ring-ink/15"
                    />
                  </td>
                ))}
                <td className="px-3 py-1.5 text-right align-middle">
                  <input
                    data-testid={`op-completion-pct-${r.project}`}
                    type="text"
                    inputMode="decimal"
                    value={r.completion_pct ?? ""}
                    onChange={(e) => setCell(r.project, "completion_pct", e.target.value)}
                    placeholder="—"
                    title="e.g. 62.5 — blank keeps the standard next-month payment plan"
                    className="cf-touch w-24 px-2 py-1.5 bg-surface rounded-lg text-sm text-right num focus:outline-none focus:ring-2 focus:ring-ink/15"
                  />
                </td>
                <td className="px-2 py-1.5 text-center align-middle">
                  <button
                    data-testid={`op-remove-${r.project}`}
                    onClick={() => removeRow(r.project)}
                    title="Remove — project falls back to the milestone engine"
                    className="cf-touch p-1 text-muted-light hover:text-red-600"
                  ><X size={13} /></button>
                </td>
              </tr>
            ))}
            {!loading && addable.length > 0 && (
              <tr className="border-b border-hairline/40 bg-gold-500/5">
                <td className="px-2 py-1.5"></td>
                <td className="px-4 py-1.5" colSpan={5}>
                  <div className="flex items-center gap-2">
                    <select
                      data-testid="op-add-select"
                      value={addSel}
                      onChange={(e) => setAddSel(e.target.value)}
                      className="cf-touch px-2 py-1.5 bg-surface rounded-lg text-xs text-ink"
                    >
                      <option value="">Select project to anchor…</option>
                      {addable.map((p) => <option key={p.id || p.name} value={p.name}>{p.name}</option>)}
                    </select>
                    <button
                      data-testid="op-add-btn"
                      onClick={addRow}
                      disabled={!addSel}
                      className="cf-touch inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md bg-ink text-paper disabled:opacity-50"
                    ><Plus size={12} /> Add project</button>
                  </div>
                </td>
                <td></td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr data-testid="op-grand-total" className="font-bold text-ink" style={{ background: "var(--cf-accent-solid)" }}>
              <td></td>
              <td className="px-4 py-2 text-sm">Grand Total</td>
              <td className="px-3 py-2 text-right num text-sm" data-testid="op-total-sales">{totals.sales_booked_till.toLocaleString("en-IN")}</td>
              <td className="px-3 py-2 text-right num text-sm" data-testid="op-total-demand">{totals.demand_raised_till.toLocaleString("en-IN")}</td>
              <td className="px-3 py-2 text-right num text-sm" data-testid="op-total-outstanding">{totals.outstanding.toLocaleString("en-IN")}</td>
              <td></td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="text-[11px] text-muted-light flex items-center gap-1.5">
        <Info size={11} /> Projects without a row here keep the milestone-engine collections model. Removing a row (✕) reverts that project to the engine.
      </p>
    </div>
  );
}

